import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { categoriesAPI, uploadsAPI } from '../services/api';
import { subcategoryPath } from '../services/catalogPath';
import { useT } from '../services/i18n';
import { catName, subName } from '../services/catalogI18n';

const CategoryGrid = () => {
  const { t, lang } = useT();
  const [categories, setCategories] = useState([]);
  const [hoveredCategory, setHoveredCategory] = useState(null);

  useEffect(() => {
    categoriesAPI.getAll().then(setCategories).catch(() => setCategories([]));
  }, []);

  return (
    <section className="py-6 sm:py-10 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
          {t('home.categoriesTitle')}
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4" data-testid="home-category-grid">
          {categories.map((category) => (
            <div
              key={category.id}
              className="relative group"
              onMouseEnter={() => setHoveredCategory(category.id)}
              onMouseLeave={() => setHoveredCategory(null)}
            >
              <Link 
                to={`/pieces/${category.slug}`}
                data-testid={`home-category-${category.slug}`}
                className="block bg-gray-50 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:bg-white border border-transparent hover:border-orange-200"
              >
                <div className="aspect-square mb-3 flex items-center justify-center">
                  <img
                    src={category.image}
                    alt={catName(category, lang)}
                    className="w-full h-full object-contain transition-transform duration-200 group-hover:scale-105"
                  />
                </div>
                <h3 className="text-sm font-medium text-gray-900 text-center leading-tight group-hover:text-orange-600 transition-colors">
                  {catName(category, lang)}
                </h3>
              </Link>

              {/* Hover dropdown */}
              {hoveredCategory === category.id && category.subcategories.length > 0 && (
                <div className="absolute z-50 left-0 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-3 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    {catName(category, lang)}
                  </div>
                  {category.subcategories.map((sub) => (
                    <Link
                      key={sub}
                      to={subcategoryPath(category, sub)}
                      className="flex items-center gap-2 py-1.5 px-2 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 rounded transition-colors"
                    >
                      {category.subcategory_images?.[sub] && <img src={uploadsAPI.resolveUrl(category.subcategory_images[sub])} alt="" className="w-7 h-7 rounded object-cover bg-gray-50 no-placeholder flex-shrink-0" />}
                      <span className="flex-1">{subName(category, sub, lang)}</span>
                      <ChevronRight size={14} className="text-gray-400" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 text-center">
          <Link to="/pieces" className="text-blue-600 hover:text-blue-700 font-medium text-sm hover:underline">
            {t('home.moreParts')}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CategoryGrid;
